import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/components/ui/use-toast";
import { Loader2, Check, AlertCircle, ShieldAlert } from "lucide-react";
import { createLogger } from "@/lib/utils/logger";

// Create a logger for tracking password reset issues
const logger = createLogger('auth', 'reset-password');

export default function ResetPassword() {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSessionChecking, setIsSessionChecking] = useState(true);
  const [hasSessionError, setHasSessionError] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Check if user is already in a password reset flow
  useEffect(() => {
    const checkResetFlow = async () => {
      try {
        logger.info("Checking password reset session");
        
        // Increase timeout for session check
        const sessionCheckPromise = supabase.auth.getSession();
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error("Session check timed out")), 10000);
        });
        
        const { data, error } = await Promise.race([
          sessionCheckPromise,
          timeoutPromise.then(() => {
            throw new Error("The server is taking too long to respond. Please try refreshing the page.");
          }),
        ]) as { data: { session: any }, error: Error | null };
        
        if (error) {
          throw error;
        }
        
        if (!data.session) {
          throw new Error("No active session found. The reset link may have expired or been used already.");
        }
        
        // Check if we're in a password recovery action
        if (!data.session?.user?.email) {
          throw new Error("User information not available. Please try requesting a new reset link.");
        }
        
        logger.info("Valid password reset session found");
        setIsSessionChecking(false);
      } catch (error) {
        const err = error as Error;
        console.error("Session check error:", err);
        logger.error(`Session check error: ${err.message}`);
        
        setHasSessionError(true);
        const errorMessage = err instanceof Error ? err.message : "Invalid or expired reset link. Please request a new password reset.";
        setError(errorMessage);
        setIsSessionChecking(false);
        
        // Redirect to forgot password after a delay
        setTimeout(() => {
          navigate("/auth/forgot-password", { replace: true });
        }, 5000);
      }
    };

    checkResetFlow();
  }, [navigate, toast]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!password || !confirmPassword) {
      setError("Please fill in all fields");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    // Basic password strength validation
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    
    if (!(hasUpperCase && hasLowerCase && hasNumbers)) {
      setError("Password must contain at least one uppercase letter, one lowercase letter, and one number");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      logger.info("Attempting to update password");
      
      // Add timeout to prevent hanging requests
      const updatePromise = supabase.auth.updateUser({ password });
      
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error("Password update timed out")), 15000);
      });
      
      const { error } = await Promise.race([
        updatePromise,
        timeoutPromise.then(() => {
          throw new Error("The server is taking too long to respond. Please try again later.");
        }),
      ]) as { error: Error | null };

      if (error) throw error;

      logger.info("Password updated successfully");
      setSuccess(true);
      
      // Redirect to login page after short delay
      setTimeout(() => {
        navigate("/auth/login", { replace: true });
      }, 3000);
    } catch (error) {
      const err = error as Error;
      console.error("Password reset error:", err);
      logger.error(`Password update error: ${err.message}`);
      
      let errorMessage = err.message;
      if (err.message.includes("timed out") || err.message.includes("504")) {
        errorMessage = "The server is currently busy. Please try again in a few minutes.";
      }
        
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (isSessionChecking) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-gradient-to-br from-primary/5 via-secondary/5 to-background">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="mt-2 text-muted-foreground font-serif">Verifying your reset link...</p>
        </div>
      </div>
    );
  }

  if (hasSessionError) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-gradient-to-br from-primary/5 via-secondary/5 to-background">
        <div className="w-full max-w-md px-4">
          <Card className="backdrop-blur-sm border-primary/20">
            <CardHeader className="space-y-1">
              <div className="flex flex-col items-center mb-4">
                <img 
                  src="/lovable-uploads/cc742b1a-3eb0-4c19-b31f-8e524aa87815.png"
                  alt="NWSISD Logo"
                  className="h-24 w-auto mb-4"
                />
              </div>
              <CardTitle className="text-2xl text-center font-title text-primary">Reset Link Expired</CardTitle>
              <CardDescription className="text-center font-sans">
                There was a problem with your reset link
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert variant="destructive">
                <ShieldAlert className="h-4 w-4 mr-2" />
                <AlertDescription className="font-serif">{error}</AlertDescription>
              </Alert>
              <div className="text-center text-muted-foreground font-serif">
                <p>Redirecting you to the password reset page in a moment...</p>
                <Loader2 className="h-4 w-4 animate-spin mx-auto mt-2" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen w-screen flex items-center justify-center bg-gradient-to-br from-primary/5 via-secondary/5 to-background">
      <div className="w-full max-w-md px-4">
        <Card className="backdrop-blur-sm border-primary/20">
          <CardHeader className="space-y-1">
            <div className="flex flex-col items-center mb-4">
              <img 
                src="/lovable-uploads/cc742b1a-3eb0-4c19-b31f-8e524aa87815.png"
                alt="NWSISD Logo"
                className="h-24 w-auto mb-4"
              />
            </div>
            <CardTitle className="text-2xl text-center font-title text-primary">Set New Password</CardTitle>
            <CardDescription className="text-center font-sans">
              Create a new password for your account
            </CardDescription>
          </CardHeader>
          {success ? (
            <CardContent className="space-y-4">
              <div className="flex flex-col items-center justify-center py-6">
                <div className="bg-green-100 p-3 rounded-full mb-4">
                  <Check className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-lg font-medium text-center font-serif">Password Reset Successful</h3>
                <p className="text-muted-foreground text-center mt-2 font-serif">
                  Your password has been updated. Redirecting to login...
                </p>
              </div>
            </CardContent>
          ) : (
            <form onSubmit={handleSubmit}>
              <CardContent className="space-y-4">
                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4 mr-2" />
                    <AlertDescription className="font-serif">{error}</AlertDescription>
                  </Alert>
                )}
                <div className="space-y-2">
                  <Label htmlFor="password" className="font-serif">New Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="Enter new password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={isLoading}
                    className="font-sans"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="font-serif">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    disabled={isLoading}
                    className="font-sans"
                  />
                </div>
                <div className="text-sm text-muted-foreground font-serif">
                  <p>Your password must contain:</p>
                  <ul className="list-disc ml-5 mt-1">
                    <li>At least 6 characters</li>
                    <li>At least one uppercase letter (A-Z)</li>
                    <li>At least one lowercase letter (a-z)</li>
                    <li>At least one number (0-9)</li>
                  </ul>
                </div>
              </CardContent>
              <CardFooter className="flex flex-col space-y-4">
                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={isLoading || !password || !confirmPassword}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      <span className="font-serif">Resetting...</span>
                    </>
                  ) : (
                    <span className="font-serif">Reset Password</span>
                  )}
                </Button>
              </CardFooter>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
}
